// featureFlags — iter104. Generic cache + reactive hook around any boolean
// admin setting exposed via /api/public-settings. Sibling to lib/waAuth.js
// but for non-auth flags so we don't have to mint a new file per toggle.
//
// Current flags:
//   - log_spend_enabled  → cached as "habano.log_spend_enabled"
//
// Defaults: FALSE (consistent with the conservative posture taken for
// WhatsApp auth). On every app mount we call `refreshFeatureFlags()` which
// updates the cache from the API and emits "habano:flags-changed".
import { useEffect, useState } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const EVT = "habano:flags-changed";

const KEYS = {
  log_spend_enabled: "habano.log_spend_enabled",
};

function readCache(name) {
  try {
    const v = localStorage.getItem(KEYS[name]);
    if (v === "true") return true;
    if (v === "false") return false;
    return null;
  } catch { return null; }
}
function writeCache(name, b) {
  try { localStorage.setItem(KEYS[name], b ? "true" : "false"); } catch { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(EVT, { detail: { [name]: b } })); }
  catch { /* ignore */ }
}

/** Sync getter — returns the cached value or FALSE if no cache exists. */
export function getFeatureFlag(name) {
  const v = readCache(name);
  return v === null ? false : v;
}

/** Fetch /public-settings, update every known flag in the cache, notify. */
export async function refreshFeatureFlags() {
  try {
    const { data } = await axios.get(`${BASE}/api/public-settings`, { timeout: 6000 });
    const next = {};
    Object.keys(KEYS).forEach((name) => {
      const raw = (data && data[name]) || "false";
      const enabled = String(raw).toLowerCase() === "true";
      writeCache(name, enabled);
      next[name] = enabled;
    });
    return next;
  } catch {
    return Object.keys(KEYS).reduce((acc, name) => {
      acc[name] = readCache(name) === true;
      return acc;
    }, {});
  }
}

/** Admin-side setter — persists via the admin/settings PUT endpoint and
 *  immediately updates the cache so the toggle reflects new state. */
export async function setFeatureFlag(api, name, enabled) {
  await api.put(`/admin/settings/${name}`, { value: enabled ? "true" : "false" });
  writeCache(name, enabled);
  return enabled;
}

/** React hook — reactive read of a single flag from the cache. */
export function useFeatureFlag(name) {
  const [enabled, setEnabled] = useState(() => getFeatureFlag(name));
  useEffect(() => {
    const handle = () => setEnabled(getFeatureFlag(name));
    window.addEventListener(EVT, handle);
    window.addEventListener("storage", handle);
    return () => {
      window.removeEventListener(EVT, handle);
      window.removeEventListener("storage", handle);
    };
  }, [name]);
  return enabled;
}

/* ── iter156 — Static compile-time flag ──────────────────────────────
 * Controls visibility of the Cubano/Tourist (a.k.a. Local/Tourist)
 * cohort toggle everywhere in the UI:
 *   • ProfileDropdown → Local/Tourist ViewToggleRow
 *   • ViewSelectorSheet popup
 *   • QR sheet cohort pill
 *   • Friend scan cohort pill
 *
 * Flip to `true` to fully restore the feature — NO other change is
 * required. Backend logic, localStorage keys, and admin settings are
 * intentionally left in place. */
export const SHOW_VIEW_TOGGLE = false;
